import { CardActionArea, Paper } from "@mui/material";
import {
  APP_STORE_URL,
  PLAY_STORE_URL,
  STORE_BADGES,
} from "Components/MobileApp/MobileAppInstallBanner/constants";
import { MobileAppInstallBannerContent } from "Components/MobileApp/MobileAppInstallBanner/MobileAppInstallBannerContent";
import { StoreBadgeImage } from "Components/MobileApp/MobileAppInstallBanner/StoreBadgeImage";
import { StoreBadgeLink } from "Components/MobileApp/MobileAppInstallBanner/StoreBadgeLink";
import { useTranslation } from "react-i18next";
import { LANGUAGE_MAPPING } from "Translations/languageConfig";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.MobileApp.InstallBanner;

export type MobileAppInstallBannerStores = "appStore" | "playStore" | "both";

type Props = {
  stores: MobileAppInstallBannerStores;
};

/**
 * Presentational store-install promo card. Platform detection lives in
 * MobileAppInstallBanner — this component only renders what it's told.
 *
 * The native iOS Smart App Banner (`apple-itunes-app` meta, app-id 6755823077)
 * was deliberately skipped: it is Safari-only and page-global, so it would
 * also appear on sign-in/sign-up, and injecting the meta tag after login is
 * officially unsupported (Safari reads it at page load).
 */
export const MobileAppInstallBannerCard = ({ stores }: Props) => {
  const { t } = useResource();
  const { i18n } = useTranslation();

  const badges = STORE_BADGES[LANGUAGE_MAPPING[i18n.language] ?? "en"];
  const storeBadges = {
    appStore: {
      href: APP_STORE_URL,
      imageSrc: badges.appStore,
      alt: t(RESOURCES.AppStoreBadgeAlt),
    },
    playStore: {
      href: PLAY_STORE_URL,
      imageSrc: badges.playStore,
      alt: t(RESOURCES.PlayStoreBadgeAlt),
    },
  };

  // Both stores: the card itself is static, each badge is its own link.
  if (stores === "both") {
    return (
      <Paper
        variant="darkGradient"
        sx={{ borderRadius: 2, overflow: "hidden" }}
      >
        <MobileAppInstallBannerContent>
          <StoreBadgeLink {...storeBadges.appStore} />
          <StoreBadgeLink {...storeBadges.playStore} />
        </MobileAppInstallBannerContent>
      </Paper>
    );
  }

  // Single store: the whole card is one link to that store; the badge is a
  // plain image so no anchor nests inside the card's anchor.
  const { href, imageSrc, alt } = storeBadges[stores];
  return (
    <Paper variant="darkGradient" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <CardActionArea
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        <MobileAppInstallBannerContent>
          <StoreBadgeImage imageSrc={imageSrc} alt={alt} />
        </MobileAppInstallBannerContent>
      </CardActionArea>
    </Paper>
  );
};
